import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import axiosInstance from "../../../../axios";
import certificateModel from "../../../../assets/images/certificate_model.png";
import {
    Award, Trophy, Star, ShieldAlert, Bookmark, Calendar,
    CheckCircle2, ChevronRight, Clipboard, Clock, Download,
    ExternalLink, Lock, Search, Share2, Sparkles, User, X, Check, Flame, BookOpen
} from "lucide-react";

export default function Achievements() {
    // Redux selector for accessing the logged-in student's info
    const { user } = useSelector((state) => state.users);
    
    // Core states
    const [activeTab, setActiveTab] = useState("all"); // "all" | "certificates" | "badges"
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedCert, setSelectedCert] = useState(null);
    const [toastMsg, setToastMsg] = useState("");
    const [purchasedCourses, setPurchasedCourses] = useState([]);
    const [loading, setLoading] = useState(true);

    // Simulated XP and Streak numbers (coupled with real course details)
    const baseXP = 1250;
    const streakDays = 12;

    // Static baseline certificates (mock achievements to showcase visual excellence)
    const defaultCertificates = [
        {
            key: "cert-0",
            label: "Javascript Programming Masterclass",
            Author: "Quazi",
            Completed_in: "7 Days",
            completed_at: "15/04/2026",
            badgeName: "Javascript Master",
            grade: "96%",
            certId: "SML-JS-847291",
            duration: "24 Hours of Content",
            skills: ["JavaScript ES6", "Async/Await", "DOM Manipulation"]
        },
        {
            key: "cert-1",
            label: "Advanced CSS, SCSS & Responsive Design",
            Author: "Falgun",
            Completed_in: "2 Days",
            completed_at: "06/04/2026",
            badgeName: "CSS King",
            grade: "92%",
            certId: "SML-CSS-394857",
            duration: "18 Hours of Content",
            skills: ["SCSS/SASS", "Flexbox & Grid", "Web Animations"]
        },
        {
            key: "cert-2",
            label: "Relational Databases & SQL Essentials",
            Author: "Shyima",
            Completed_in: "3 Days",
            completed_at: "11/04/2026",
            badgeName: "Pro-Player of Sql",
            grade: "88%",
            certId: "SML-SQL-102948",
            duration: "12 Hours of Content",
            skills: ["DBMS", "SQL Queries", "Database Design"]
        }
    ];

    // Static gamified badges dataset (using silver/slate/zinc gradients for a metallic feel)
    const badgesList = [
        {
            id: "badge-js",
            title: "JavaScript Master",
            desc: "Completed Javascript Masterclass with > 90% score.",
            unlocked: true,
            icon: <Award className="w-8 h-8 text-slate-700" />,
            color: "from-slate-300 via-slate-400 to-slate-500",
            unlockedAt: "15 Apr 2026"
        },
        {
            id: "badge-css",
            title: "CSS King",
            desc: "Designed exceptional layouts with SCSS in record time.",
            unlocked: true,
            icon: <Trophy className="w-8 h-8 text-zinc-700" />,
            color: "from-zinc-300 via-zinc-400 to-zinc-500",
            unlockedAt: "06 Apr 2026"
        },
        {
            id: "badge-sql",
            title: "Pro Sql Player",
            desc: "Optimized complex subqueries and structured relational databases.",
            unlocked: true,
            icon: <Star className="w-8 h-8 text-stone-750" />,
            color: "from-stone-300 via-stone-400 to-stone-500",
            unlockedAt: "11 Apr 2026"
        },
        {
            id: "badge-fast",
            title: "Speed Demon",
            desc: "Completed a full course in less than 3 days.",
            unlocked: true,
            icon: <Flame className="w-8 h-8 text-slate-800" />,
            color: "from-slate-400 to-zinc-600",
            unlockedAt: "06 Apr 2026"
        },
        {
            id: "badge-perfect",
            title: "Absolute Perfection",
            desc: "Score exactly 100% on any lesson assessment quiz.",
            unlocked: false,
            icon: <Sparkles className="w-8 h-8 text-slate-300" />,
            color: "from-slate-200 to-slate-300",
            req: "Score 100% in a quiz"
        },
        {
            id: "badge-explorer",
            title: "Daily Champion",
            desc: "Maintained a continuous daily learning streak of 10+ days.",
            unlocked: true,
            icon: <CheckCircle2 className="w-8 h-8 text-slate-600" />,
            color: "from-zinc-200 via-zinc-350 to-slate-400",
            unlockedAt: "25 May 2026"
        },
        {
            id: "badge-ai",
            title: "AI Trailblazer",
            desc: "Utilize AI-guided learning pathways on the Smik platform.",
            unlocked: true,
            icon: <Bookmark className="w-8 h-8 text-slate-850" />,
            color: "from-slate-350 via-slate-450 to-slate-550",
            unlockedAt: "28 May 2026"
        },
        {
            id: "badge-veteran",
            title: "Master Scholar",
            desc: "Finish 5 full catalog courses to unlock professional validation.",
            unlocked: false,
            icon: <Lock className="w-8 h-8 text-slate-300" />,
            color: "from-slate-200 to-slate-300",
            req: "Complete 5 courses (3/5 done)"
        }
    ];

    // Core Mastered Skills Matrix (using sophisticated shades of slate and charcoal)
    const skillsMatrix = [
        { name: "JavaScript ES6+", level: "Expert", percent: 96, color: "bg-slate-800" },
        { name: "HTML5 & CSS3/SCSS", level: "Expert", percent: 92, color: "bg-slate-700" },
        { name: "SQL & Databases", level: "Advanced", percent: 88, color: "bg-slate-600" },
        { name: "React Application Architecture", level: "Intermediate", percent: 65, color: "bg-slate-500" },
        { name: "Node.js & Restful APIs", level: "Intermediate", percent: 45, color: "bg-slate-400" }
    ];

    // Fetch purchased courses dynamically
    useEffect(() => {
        const fetchCourses = async () => {
            try {
                const res = await axiosInstance.get("/course/purchased");
                const fetched = res?.data?.data?.data || [];
                setPurchasedCourses(fetched);
            } catch (error) {
                console.error("Error fetching purchased courses:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchCourses();
    }, []);

    // Helper to calculate statistics
    const unlockedBadges = badgesList.filter(b => b.unlocked).length;
    const totalBadgesCount = badgesList.length;
    
    // Combine dynamic completed purchased courses with default certificates
    const dynamicCompletedCerts = purchasedCourses
        .filter(c => c.progress && 
                     Number(c.progress.totalSection) > 0 && 
                     Number(c.progress.finishedSection) >= Number(c.progress.totalSection))
        .map((c, i) => ({
            key: `dyn-cert-${c.ID || i}`,
            label: c.CourseName || "Professional Course",
            Author: c.InstructorName || "SMIK Faculty",
            Completed_in: "Flexible",
            completed_at: new Date().toLocaleDateString(),
            badgeName: "SMIK Graduate",
            grade: "Passed",
            certId: `SML-DYN-${c.ID || 100 + i}`,
            duration: c.Duration ? `${c.Duration} Hours of Content` : "Self-Paced Learning",
            skills: [c.Category || "GenAI", c.SubCategory || "Artificial Intelligence"].filter(Boolean)
        }));

    const allCertificates = [...dynamicCompletedCerts, ...defaultCertificates];
    const totalCertsCount = allCertificates.length;

    // Filter certificates and badges based on query and tab
    const filteredCerts = allCertificates.filter(c =>
        c.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.Author.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const filteredBadges = badgesList.filter(b =>
        b.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        b.desc.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Clipboard Copy Helper
    const copyToClipboard = (url) => {
        navigator.clipboard.writeText(url);
        setToastMsg("Verification link copied to clipboard!");
        setTimeout(() => setToastMsg(""), 3000);
    };

    // Print Handler
    const handlePrint = () => {
        window.print();
    };

    // Get greeting name
    const studentGreetingName = user?.firstName
        ? `${user.firstName} ${user.lastName || ""}`
        : "Student";

    return (
        <div className="min-h-screen bg-[#f1f5f9] py-8 px-4 md:px-8 font-sans">
            {/* Embedded styles to support gorgeous printing of certificates ONLY */}
            <style>{`
                @page {
                    size: A4 landscape;
                    margin: 0;
                }
                @media print {
                    body * {
                        visibility: hidden !important;
                    }
                    #printable-certificate, #printable-certificate * {
                        visibility: visible !important;
                    }
                    #printable-certificate {
                        position: fixed !important;
                        left: 0 !important;
                        top: 0 !important;
                        width: 297mm !important; /* A4 landscape width */
                        height: 210mm !important; /* A4 landscape height */
                        margin: 0 !important;
                        padding: 0 !important;
                        box-shadow: none !important;
                        border: none !important;
                        background-image: url(${certificateModel}) !important;
                        background-size: cover !important;
                        background-position: center !important;
                        background-repeat: no-repeat !important;
                        -webkit-print-color-adjust: exact !important;
                        print-color-adjust: exact !important;
                        z-index: 99999 !important;
                        display: flex !important;
                        flex-direction: column !important;
                        justify-content: center !important;
                        align-items: center !important;
                    }
                    .no-print {
                        display: none !important;
                    }
                }
            `}</style>

            <div className="max-w-6xl mx-auto">
                {/* Custom Alert/Toast Notification */}
                {toastMsg && (
                    <div className="fixed top-5 right-5 bg-zinc-900 text-white px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-3 z-50 animate-in fade-in slide-in-from-top-4 duration-300 font-bold text-sm">
                        <Check className="text-slate-300 w-5 h-5 stroke-[3]" />
                        <span>{toastMsg}</span>
                    </div>
                )}

                {/* Hero Header (Slate/Zinc Charcoal theme) */}
                <div className="bg-gradient-to-r from-slate-700 via-slate-800 to-slate-900 rounded-[2.5rem] p-8 md:p-12 text-white shadow-xl mb-8 relative overflow-hidden">
                    {/* Background decorations */}
                    <div className="absolute right-0 bottom-0 w-80 h-80 bg-slate-500/10 rounded-full blur-3xl pointer-events-none"></div>
                    <div className="absolute left-1/3 top-0 w-40 h-40 bg-zinc-550/10 rounded-full blur-2xl pointer-events-none"></div>
                    
                    <div className="relative z-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                        <div>
                            <div className="inline-flex items-center gap-2 bg-white/10 px-4 py-1.5 rounded-full text-slate-100 border border-white/5 font-black text-xs uppercase tracking-widest mb-4">
                                <Sparkles className="w-3.5 h-3.5 fill-slate-100" />
                                Verified Learning Milestones
                            </div>
                            <h1 className="text-3xl md:text-5xl font-black tracking-tight leading-tight mb-3">
                                Congratulations, {studentGreetingName}!
                            </h1>
                            <p className="text-slate-200/90 text-sm md:text-base font-semibold max-w-xl leading-relaxed">
                                You have unlocked professional validation and gamified achievements. Keep completing quiz assessments and courses to level up your expertise.
                            </p>
                        </div>
                        <div className="flex-shrink-0 bg-white/10 backdrop-blur-md border border-white/10 rounded-3xl p-6 text-center">
                            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-300">Current Level</p>
                            <h3 className="text-5xl font-black text-slate-100 mt-1">Level 4</h3>
                            <div className="w-32 h-2 bg-white/20 rounded-full mt-3 overflow-hidden">
                                <div className="h-full bg-slate-350 rounded-full w-3/4"></div>
                            </div>
                            <p className="text-[10px] font-bold text-slate-300/80 mt-1.5">1,250 / 2,000 XP</p>
                        </div>
                    </div>
                </div>

                {/* Stats Summary Grid */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8">
                    <div className="bg-white rounded-3xl border border-slate-200/60 p-6 flex items-center gap-5 hover:shadow-md transition-all shadow-sm">
                        <div className="w-12 h-12 bg-slate-100 text-slate-700 rounded-2xl flex items-center justify-center flex-shrink-0">
                            <Award className="w-6 h-6" />
                        </div>
                        <div>
                            <p className="text-slate-400 text-[10px] font-black uppercase tracking-wider">Certificates</p>
                            <p className="text-2xl font-black text-slate-800 leading-tight mt-0.5">{totalCertsCount}</p>
                            <p className="text-slate-400 text-[10px] font-medium mt-0.5">Fully Verified</p>
                        </div>
                    </div>

                    <div className="bg-white rounded-3xl border border-slate-200/60 p-6 flex items-center gap-5 hover:shadow-md transition-all shadow-sm">
                        <div className="w-12 h-12 bg-zinc-100 text-zinc-700 rounded-2xl flex items-center justify-center flex-shrink-0">
                            <Trophy className="w-6 h-6" />
                        </div>
                        <div>
                            <p className="text-slate-400 text-[10px] font-black uppercase tracking-wider">Badges Unlocked</p>
                            <p className="text-2xl font-black text-slate-800 leading-tight mt-0.5">{unlockedBadges}/{totalBadgesCount}</p>
                            <p className="text-slate-400 text-[10px] font-medium mt-0.5">XP Boost Activated</p>
                        </div>
                    </div>

                    <div className="bg-white rounded-3xl border border-slate-200/60 p-6 flex items-center gap-5 hover:shadow-md transition-all shadow-sm">
                        <div className="w-12 h-12 bg-slate-100 text-slate-650 rounded-2xl flex items-center justify-center flex-shrink-0">
                            <Sparkles className="w-6 h-6" />
                        </div>
                        <div>
                            <p className="text-slate-400 text-[10px] font-black uppercase tracking-wider">Total XP</p>
                            <p className="text-2xl font-black text-slate-800 leading-tight mt-0.5">2,450 XP</p>
                            <p className="text-slate-400 text-[10px] font-medium mt-0.5">Rank: Top 5%</p>
                        </div>
                    </div>

                    <div className="bg-white rounded-3xl border border-slate-200/60 p-6 flex items-center gap-5 hover:shadow-md transition-all shadow-sm">
                        <div className="w-12 h-12 bg-zinc-100 text-zinc-600 rounded-2xl flex items-center justify-center flex-shrink-0">
                            <Flame className="w-6 h-6 fill-slate-700/10" />
                        </div>
                        <div>
                            <p className="text-slate-400 text-[10px] font-black uppercase tracking-wider">Active Streak</p>
                            <p className="text-2xl font-black text-slate-800 leading-tight mt-0.5">{streakDays} Days</p>
                            <p className="text-slate-400 text-[10px] font-medium mt-0.5">Multiplier: 1.5x</p>
                        </div>
                    </div>
                </div>

                {/* Main Content Layout Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Left Column: Filter Controls and Achievements Grid (takes 2 cols) */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Control Bar */}
                        <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-4 bg-white p-3 rounded-[1.5rem] border border-slate-200/60 shadow-sm">
                            {/* Filter Buttons */}
                            <div className="flex items-center bg-slate-50 p-1 rounded-xl">
                                <button
                                    onClick={() => setActiveTab("all")}
                                    className={`px-4 py-2 text-xs font-black uppercase tracking-wider rounded-lg transition-all ${
                                        activeTab === "all"
                                            ? "bg-slate-850 text-white shadow-sm"
                                            : "text-slate-500 hover:text-slate-850"
                                    }`}
                                >
                                    All
                                </button>
                                <button
                                    onClick={() => setActiveTab("certificates")}
                                    className={`px-4 py-2 text-xs font-black uppercase tracking-wider rounded-lg transition-all ${
                                        activeTab === "certificates"
                                            ? "bg-slate-850 text-white shadow-sm"
                                            : "text-slate-500 hover:text-slate-850"
                                    }`}
                                >
                                    Certificates
                                </button>
                                <button
                                    onClick={() => setActiveTab("badges")}
                                    className={`px-4 py-2 text-xs font-black uppercase tracking-wider rounded-lg transition-all ${
                                        activeTab === "badges"
                                            ? "bg-slate-850 text-white shadow-sm"
                                            : "text-slate-500 hover:text-slate-850"
                                    }`}
                                >
                                    Badges
                                </button>
                            </div>

                            {/* Search Box */}
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                                <input
                                    type="text"
                                    placeholder="Search credentials..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full sm:w-60 pl-9 pr-4 py-2 bg-white border border-slate-250 rounded-xl text-xs font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-850/10 focus:border-slate-850 transition-all shadow-sm"
                                />
                            </div>
                        </div>

                        {/* Certificates Grid Container */}
                        {(activeTab === "all" || activeTab === "certificates") && (
                            <div className="space-y-4">
                                <h3 className="text-xl font-black text-slate-800 px-1 flex items-center gap-2">
                                    <Award className="w-5 h-5 text-slate-700" />
                                    Earned Certificates
                                </h3>
                                
                                {filteredCerts.length > 0 ? (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        {filteredCerts.map((cert) => (
                                            <div
                                                key={cert.key}
                                                className="bg-white rounded-3xl border border-slate-200/60 shadow-sm p-6 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col justify-between group"
                                            >
                                                <div>
                                                    <div className="flex justify-between items-start mb-4">
                                                        <div className="w-10 h-10 bg-slate-100 text-slate-700 rounded-xl flex items-center justify-center border border-slate-200">
                                                            <Award className="w-5 h-5" />
                                                        </div>
                                                        <span className="text-[10px] font-bold text-slate-750 bg-slate-100 px-2 py-0.5 rounded border border-slate-200/80 uppercase tracking-wide">
                                                            Grade: {cert.grade}
                                                        </span>
                                                    </div>

                                                    <h4 className="text-base font-black text-slate-800 mb-2 leading-snug group-hover:text-slate-800 transition-colors min-h-[44px] line-clamp-2">
                                                        {cert.label}
                                                    </h4>

                                                    <div className="flex items-center gap-2 text-slate-400 text-xs font-medium mb-4">
                                                        <Clock className="w-3.5 h-3.5 text-slate-300" />
                                                        <span>{cert.duration}</span>
                                                    </div>

                                                    <div className="flex flex-wrap gap-1.5 mb-4">
                                                        {cert.skills.map((skill, idx) => (
                                                            <span key={idx} className="bg-slate-50 border border-slate-200/60 text-slate-550 text-[10px] font-bold px-2 py-0.5 rounded-md">
                                                                {skill}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>

                                                <div className="border-t border-slate-100 pt-4 flex items-center justify-between mt-auto">
                                                    <div>
                                                        <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">Instructor</p>
                                                        <p className="text-xs font-bold text-slate-700 mt-0.5">{cert.Author}</p>
                                                    </div>
                                                    <button
                                                        onClick={() => setSelectedCert(cert)}
                                                        className="px-4 py-2 bg-slate-800 hover:bg-slate-900 text-white text-xs font-black uppercase tracking-widest rounded-xl transition-all shadow-sm active:scale-95 flex items-center gap-1.5"
                                                    >
                                                        View Certificate <ExternalLink className="w-3 h-3" />
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="bg-white rounded-3xl border border-slate-200/60 p-12 text-center shadow-sm">
                                        <Award className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                                        <h5 className="text-base font-bold text-slate-700">No certificates found</h5>
                                        <p className="text-slate-400 text-xs max-w-xs mx-auto mt-1 leading-relaxed">
                                            Try modifying your search query or finish dynamic courses to obtain new credentials.
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Badges Gallery Container */}
                        {(activeTab === "all" || activeTab === "badges") && (
                            <div className="space-y-4 pt-2">
                                <h3 className="text-xl font-black text-slate-800 px-1 flex items-center gap-2">
                                    <Trophy className="w-5 h-5 text-slate-700" />
                                    Achievement Badges
                                </h3>

                                {filteredBadges.length > 0 ? (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        {filteredBadges.map((badge) => (
                                            <div
                                                key={badge.id}
                                                className={`bg-white rounded-3xl border p-5 flex items-start gap-4 transition-all relative overflow-hidden ${
                                                    badge.unlocked
                                                        ? "border-slate-200/80 shadow-sm hover:shadow-md"
                                                        : "border-slate-200 opacity-70 bg-slate-50/50"
                                                }`}
                                            >
                                                {/* Left status badge glow */}
                                                {badge.unlocked && (
                                                    <div className="absolute left-0 top-0 w-1 h-full bg-slate-500"></div>
                                                )}

                                                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 bg-slate-50 border relative ${
                                                    badge.unlocked ? "border-slate-200 shadow-inner" : "border-slate-200"
                                                }`}>
                                                    {badge.icon}
                                                    {!badge.unlocked && (
                                                        <div className="absolute -right-1 -bottom-1 bg-slate-700 text-white rounded-full p-1 border border-white">
                                                            <Lock className="w-2.5 h-2.5" />
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="space-y-1 pr-2">
                                                    <div className="flex items-center gap-2">
                                                        <h4 className="text-sm font-black text-slate-800 leading-tight">
                                                            {badge.title}
                                                        </h4>
                                                        {badge.unlocked && (
                                                            <span className="text-[9px] font-black bg-slate-100 text-slate-750 border border-slate-200 px-1.5 py-0.5 rounded uppercase tracking-wider">
                                                                Unlocked
                                                            </span>
                                                        )}
                                                    </div>
                                                    <p className="text-slate-500 text-xs leading-snug font-medium">
                                                        {badge.desc}
                                                    </p>
                                                    {badge.unlocked ? (
                                                        <div className="flex items-center gap-1 text-[10px] text-slate-400 font-bold pt-1.5">
                                                            <Calendar className="w-3 h-3" />
                                                            <span>Unlocked on {badge.unlockedAt}</span>
                                                        </div>
                                                    ) : (
                                                        <div className="flex items-center gap-1 text-[10px] text-slate-700 font-black pt-1.5 uppercase tracking-wide">
                                                            <span>Req: {badge.req}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="bg-white rounded-3xl border border-slate-200/60 p-12 text-center shadow-sm">
                                        <Trophy className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                                        <h5 className="text-base font-bold text-slate-700">No badges found</h5>
                                        <p className="text-slate-400 text-xs max-w-xs mx-auto mt-1 leading-relaxed">
                                            Try modifying your search or completing other achievements to unlock additional badges.
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Right Column: Skill Matrix & Timeline (takes 1 col) */}
                    <div className="space-y-6">
                        {/* Skills Competency Progress Matrix */}
                        <div className="bg-white rounded-3xl border border-slate-200/60 p-6 shadow-sm">
                            <h3 className="text-lg font-black text-slate-800 mb-5 flex items-center gap-2 pb-3 border-b border-slate-50">
                                <Sparkles className="w-5 h-5 text-slate-650" />
                                Skills Competency
                            </h3>
                            <div className="space-y-4">
                                {skillsMatrix.map((skill, index) => (
                                    <div key={index} className="space-y-1.5">
                                        <div className="flex justify-between items-center text-xs font-bold">
                                            <span className="text-slate-700">{skill.name}</span>
                                            <span className="text-slate-400 uppercase tracking-widest text-[9px]">{skill.level} ({skill.percent}%)</span>
                                        </div>
                                        <div className="w-full h-2.5 bg-slate-100 rounded-full overflow-hidden flex">
                                            <div
                                                className={`h-full rounded-full ${skill.color} transition-all duration-700`}
                                                style={{ width: `${skill.percent}%` }}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                            
                            <div className="bg-slate-50 rounded-2xl p-4 mt-6 border border-slate-200/60">
                                <p className="text-xs font-black text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
                                    <Award className="w-4 h-4" /> Next Badge Goal
                                </p>
                                <p className="text-slate-600 text-xs font-medium mt-1 leading-relaxed">
                                    Score 100% on any database exam to automatically claim the <strong className="text-slate-800">Absolute Perfection</strong> badge.
                                </p>
                            </div>
                        </div>

                        {/* Recent Unlocks / Milestones Feed */}
                        <div className="bg-white rounded-3xl border border-slate-200/60 p-6 shadow-sm">
                            <h3 className="text-lg font-black text-slate-800 mb-5 flex items-center gap-2 pb-3 border-b border-slate-50">
                                <Clock className="w-5 h-5 text-slate-600" />
                                Recent Activity
                            </h3>

                            <div className="flow-root">
                                <ul className="-mb-8">
                                    <li>
                                        <div className="relative pb-8">
                                            <span className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-slate-100" aria-hidden="true" />
                                            <div className="relative flex space-x-3">
                                                <div>
                                                    <span className="h-8 w-8 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center ring-8 ring-white">
                                                        <Bookmark className="w-4 h-4 text-slate-600" />
                                                    </span>
                                                </div>
                                                <div className="flex-1 min-w-0 pt-1.5">
                                                    <p className="text-xs font-bold text-slate-700">
                                                        Unlocked <span className="text-slate-800 font-extrabold">AI Trailblazer</span> Badge
                                                    </p>
                                                    <p className="text-[10px] text-slate-400 mt-0.5">28 May 2026</p>
                                                </div>
                                            </div>
                                        </div>
                                    </li>

                                    <li>
                                        <div className="relative pb-8">
                                            <span className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-slate-100" aria-hidden="true" />
                                            <div className="relative flex space-x-3">
                                                <div>
                                                    <span className="h-8 w-8 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center ring-8 ring-white">
                                                        <CheckCircle2 className="w-4 h-4 text-slate-600" />
                                                    </span>
                                                </div>
                                                <div className="flex-1 min-w-0 pt-1.5">
                                                    <p className="text-xs font-bold text-slate-700">
                                                        Unlocked <span className="text-slate-800 font-extrabold">Daily Champion</span> Badge
                                                    </p>
                                                    <p className="text-[10px] text-slate-400 mt-0.5">25 May 2026</p>
                                                </div>
                                            </div>
                                        </div>
                                    </li>

                                    <li>
                                        <div className="relative pb-8">
                                            <div className="relative flex space-x-3">
                                                <div>
                                                    <span className="h-8 w-8 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center ring-8 ring-white">
                                                        <Award className="w-4 h-4 text-slate-650" />
                                                    </span>
                                                </div>
                                                <div className="flex-1 min-w-0 pt-1.5">
                                                    <p className="text-xs font-bold text-slate-700">
                                                        Earned <span className="text-slate-800 font-extrabold">Javascript Programming</span> Certificate
                                                    </p>
                                                    <p className="text-[10px] text-slate-400 mt-0.5">15 Apr 2026</p>
                                                </div>
                                            </div>
                                        </div>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* High-Fidelity Printable Certificate Modal */}
            {selectedCert && (
                <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto no-print">
                    <div className="bg-white rounded-[2rem] shadow-2xl max-w-4xl w-full border border-slate-200/80 overflow-hidden animate-in zoom-in-95 duration-200 my-8">
                        {/* Modal Header */}
                        <div className="px-8 py-5 bg-slate-50 border-b border-slate-100 flex justify-between items-center no-print">
                            <button
                                onClick={() => setSelectedCert(null)}
                                className="flex items-center gap-2 px-4 py-2 hover:bg-slate-200 text-slate-700 font-bold rounded-xl text-xs uppercase tracking-wider transition-all"
                            >
                                <ChevronRight className="w-4 h-4 rotate-180" /> Back
                            </button>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={handlePrint}
                                    className="flex items-center gap-1.5 px-4 py-2 bg-slate-800 hover:bg-slate-900 text-white font-bold rounded-xl text-xs uppercase tracking-wider transition-all shadow-sm"
                                >
                                    <Download className="w-3.5 h-3.5" /> Download
                                </button>
                                <button
                                    onClick={() => setSelectedCert(null)}
                                    className="p-2 hover:bg-slate-200 text-slate-400 hover:text-slate-700 rounded-full transition-all"
                                    aria-label="Close modal"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                        </div>

                        {/* Certificate Render Container */}
                        <div className="p-4 sm:p-8 flex justify-center bg-slate-100 overflow-x-auto">
                            <div
                                id="printable-certificate"
                                className="w-full aspect-[1.414] max-w-[800px] shadow-2xl relative select-none overflow-hidden rounded-lg bg-white"
                                style={{
                                    backgroundImage: `url(${certificateModel})`,
                                    backgroundSize: "contain",
                                    backgroundPosition: "center",
                                    backgroundRepeat: "no-repeat"
                                }}
                            >
                                {/* Student Name Overlaid */}
                                <div 
                                    className="absolute text-center" 
                                    style={{
                                        top: "47%",
                                        left: "50%",
                                        transform: "translate(-50%, -50%)",
                                        width: "80%",
                                    }}
                                >
                                    <h3 
                                        className="font-serif italic text-base sm:text-2xl md:text-3xl lg:text-4xl text-slate-808 font-extrabold"
                                        style={{ fontFamily: "'Playfair Display', 'Georgia', serif" }}
                                    >
                                        {studentGreetingName}
                                    </h3>
                                </div>

                                {/* Course Name Overlaid */}
                                <div 
                                    className="absolute text-center" 
                                    style={{
                                        top: "60.5%",
                                        left: "50%",
                                        transform: "translate(-50%, -50%)",
                                        width: "80%",
                                    }}
                                >
                                    <h4 className="text-xs sm:text-base md:text-lg lg:text-xl font-black text-slate-900 tracking-tight leading-tight">
                                        {selectedCert.label}
                                    </h4>
                                </div>

                                {/* Date Overlaid */}
                                <div 
                                    className="absolute text-center" 
                                    style={{
                                        bottom: "19%",
                                        left: "26.5%",
                                        transform: "translateX(-50%)",
                                    }}
                                >
                                    <span className="text-[6px] sm:text-[9px] md:text-xs font-bold text-slate-700 font-serif italic">
                                        {selectedCert.completed_at}
                                    </span>
                                </div>

                                {/* Instructor Name Overlaid */}
                                <div 
                                    className="absolute text-center" 
                                    style={{
                                        bottom: "19%",
                                        right: "26.5%",
                                        transform: "translateX(50%)",
                                        width: "120px"
                                    }}
                                >
                                    <span className="text-[6px] sm:text-[9px] md:text-xs font-bold text-slate-700 font-serif italic block truncate">
                                        {selectedCert.Author}
                                    </span>
                                </div>

                                {/* Verification ID Overlaid */}
                                <div 
                                    className="absolute text-center" 
                                    style={{
                                        bottom: "8%",
                                        left: "50%",
                                        transform: "translateX(-50%)",
                                    }}
                                >
                                    <span className="text-[6px] sm:text-[8px] md:text-[9px] font-mono font-bold text-slate-550 uppercase tracking-widest bg-white/80 px-1.5 py-0.5 rounded border border-slate-150">
                                        Verification ID: {selectedCert.certId}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Modal Footer Controls */}
                        <div className="px-8 py-5 bg-slate-50 border-t border-slate-100 flex flex-col sm:flex-row gap-3 sm:justify-between items-center">
                            <div className="text-slate-450 text-xs font-semibold text-center sm:text-left">
                                Verification ID: <span className="text-slate-750 font-bold">{selectedCert.certId}</span>
                            </div>
                            <div className="flex gap-2 w-full sm:w-auto justify-end">
                                <button
                                    onClick={() => setSelectedCert(null)}
                                    className="px-5 py-2.5 bg-white border border-slate-200 hover:bg-slate-100 text-slate-700 text-xs font-black uppercase tracking-wider rounded-xl transition-all flex items-center gap-1.5"
                                >
                                    ← Back
                                </button>
                                <button
                                    onClick={handlePrint}
                                    className="px-5 py-2.5 bg-slate-800 hover:bg-slate-900 text-white text-xs font-black uppercase tracking-wider rounded-xl transition-all shadow-md flex items-center gap-1.5"
                                >
                                    <Download className="w-4 h-4" /> Download PDF / Print
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}


